import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationChangeType, IntegrationItemServiceInf, SpecificIntegrationActionType, StatusString } from '../interfaces/integrations';
import { Statement } from '@mitel/cloudlink-sdk/admin';
import { AdminService } from 'src/app/core/services/admin.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { Account } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { SpinnerService } from './spinner.service';
import { WorkflowService } from './workflow.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import { MSTeamsSharedService } from './msteams-shared.service';

@Injectable({
	providedIn: 'root'
})

export class IntegPhonePresenceService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private spinnerService: SpinnerService;
	private workflowService: WorkflowService;
	private msteamsSharedService: MSTeamsSharedService;
	public dialog: MatDialog;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.workflowService )
			this.workflowService = AppInjector.injector.get(WorkflowService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.msteamsSharedService )
			this.msteamsSharedService = AppInjector.injector.get(MSTeamsSharedService);
	}
	
	canBeSelected(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}
	
	isIntegrationStateChangeable(account: Account): Promise<boolean>
	{
		return Promise.resolve(true);
	}
	
	isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeamsPresence )
			{
				if ( tags.MSTeamsPresence.enabled == true )
					return true;
				return false;
			}
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}

	getConfigurationDialog() : any
	{
		return undefined;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		return Promise.resolve(true);
	}

	// purpose of this method is to let the base child integration know if it should notify the parent it changed
	// We don't want that to be automatic because some of the subfeatures are benign and would not
	// change the current view of the accounts app so we do not want a refresh on every toggle
	// maybe in the future if we refactor some of the integration framework, this could be handled in the 
	// stage change method? More analysis required
	shouldNotifyParentOfChange( account: Account, changeType: IntegrationChangeType ): Promise<any>
	{
		this.initServices();

		if ( changeType == IntegrationChangeType.ENABLED_STATE )
		{
			return Promise.resolve({product: this, reason: "refreshOwnIntegration", actionType: SpecificIntegrationActionType.ENABLE_AND_REFRESH});
		}
		else if ( changeType == IntegrationChangeType.DISABLED_STATE )
		{
			return Promise.resolve({product: this, reason: "refreshOwnIntegration", actionType: SpecificIntegrationActionType.DISABLE_AND_REFRESH});
		}
		return Promise.resolve(undefined);
	}

	canChangeIntegrationState(account: Account, state: boolean): Promise<boolean>
	{
		this.initServices();
		return this.msteamsSharedService.confirmPresenceFeatureStateChange( account, state );
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();

		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
			console.log("Here is the account before changes", newAccount)
			if ( state == true )
			{
				// only clone the workflow if it does not exist yet
				// if it exists just enable it
				if ( newAccount.tags && newAccount.tags.MSTeamsPresence && newAccount.tags.MSTeamsPresence.activityId )
				{
					// this accounts presence was cloned and set up at some point
					// there is an existing cloned workflow so enable it
					return this.msteamsSharedService.enableCloudLinkToMSTeamsPresenceWorkflow( newAccount, true ).then( result => {
						this.spinnerService.hideSpinner(); 
						return result;
					});
				}
				// we have to be specific in this test because for some reason it is getting called after the deleted 
				// and the WF is being recreated... need to investigate this after POC
				else if ( newAccount.tags && newAccount.tags.MSTeams && newAccount.tags.MSTeams.setupComplete == true )// here we will clone the worflow for the first time for this account
				{
					return this.createMSTeamsWorkflow( newAccount ).then( result => {
						this.spinnerService.hideSpinner(); 
						return result;
					})
					.catch(err => {
						this.spinnerService.hideSpinner(); 
						console.log("Failed to create the workflow", err);

						if ( err && err.body && err.body.message && err.body.message.indexOf("missing MS teams integration") != -1 )
						{
							const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.ERROR_MISSING_CONNECTOR } });
							return dialogRef.afterClosed().toPromise().then(result => {
								throw(err);
							});
						}
						else
						{
							const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.ERROR_WORKFLOW_CREATION } });
							return dialogRef.afterClosed().toPromise().then(result => {
								throw(err);
							});
						}
					});
				}
				else
					return;
			}
			else
			{
				// here we will turn off the workflow for the account
				if ( newAccount.tags && newAccount.tags.MSTeamsPresence && newAccount.tags.MSTeamsPresence.activityId )
				{
					// this accounts presence was enabled at some point
					// there is an existing cloned workflow so disable it
					return this.msteamsSharedService.enableCloudLinkToMSTeamsPresenceWorkflow( newAccount, false ).then( result => {
						this.spinnerService.hideSpinner(); 
						return result;
					});
				}
			}
		}).catch(err => {
			console.log("Failed to handle the state change for the presence feature", state, err);
			this.spinnerService.hideSpinner();
			throw err;
		});
	}

	// When the parent is removed, this is the method called so the child can clean up as well
	// remove the workflow configuration
	removeAsASubintegration?( account: Account ): Promise<any>
	{
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();

		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			console.log("Here is the account before changes", newAccount)

			// clean up the presence workflow and tags
			if ( newAccount.tags && newAccount.tags.MSTeamsPresence && newAccount.tags.MSTeamsPresence.activityId )
			{
				// everything was setup so delete it all
				try
				{
					await this.workflowService.deleteMSTeamsPresenceWorkflowAndAll(
						newAccount.tags.MSTeamsPresence.activityId,
						newAccount.tags.MSTeamsPresence.clientId,
						newAccount.tags.MSTeamsPresence.policyId,
						account.accountId);
				}
				catch (e)
				{
					// delete is always best effort so it would not be a failure if something did not delete 
					// properly
					console.log("There was a delete error when removing the ms teams presence workflow, continuing", e);
				}

				try
				{
					await this.adminService.deleteAccountTag( account.accountId, 'MSTeamsPresence' );
				}
				catch (e)
				{
					// delete is always best effort so it would not be a failure if something did not delete 
					// properly
					console.log("There was a delete error when removing the ms teams presence tag", e);
				}
			}
			this.spinnerService.hideSpinner();
			return;
		})
		.catch(err => {
			console.log("Failed to get the account", err);
			this.spinnerService.hideSpinner();
			return;
		});
	}

	handleEventFromAnotherIntegration( account: Account, event: any ): Promise<any>
	{
		this.initServices();

		if ( event && (event.actionType == SpecificIntegrationActionType.ENABLE_AND_REFRESH || 
				event.actionType == SpecificIntegrationActionType.DISABLE_AND_REFRESH) )
		{
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
			this.spinnerService.showSpinner();
			return this.adminService.getAccountTags( account.accountId ).then( tags => {
				this.spinnerService.hideSpinner();
				// if the integration has never been setup there is nothing to do
				if ( tags && tags.MSTeams && tags.MSTeams.setupComplete == true )
				{
					if ( tags.MSTeamsPresence && tags.MSTeamsPresence.enabled == true && event.actionType == SpecificIntegrationActionType.DISABLE_AND_REFRESH )
					{
						// if already disabled, skip it
						return this.handleIntegrationStateChange( account, false ).then(result => {
							let event: any = {
								product: this,
								reason: "refreshOwnIntegration"
							}
							return event;
						});
					} 
					else if ( event.actionType == SpecificIntegrationActionType.ENABLE_AND_REFRESH && 
						( (tags.MSTeamsPresence && tags.MSTeamsPresence.enabled == false) || !tags.MSTeamsPresence ))
					{
						// if already enabled, skip it
						return this.handleIntegrationStateChange( account, true ).then(result => {
							let event: any = {
								product: this,
								reason: "refreshOwnIntegration"
							}
							return event;
						});
					}
					else
						Promise.resolve();
				}
				else
					Promise.resolve();
			})
			.catch(err => {
				console.error('handleEventFromAnotherIntegration Failed to handle request', err);
				this.spinnerService.hideSpinner();
				throw (err);
			});
		}
		else
			Promise.resolve();
	}
	
	private createMSTeamsWorkflow( account: Account ): Promise<any>
	{
		// once created we will save the id in the account tags for use later

		return this.workflowService.createMSTeamsPresenceWorkflow( 
			account.tags.MSTeams.connectionId, account.accountId ).then( workflowData => {					

			let dataChanges = {
				tags: { 
					MSTeamsPresence: {
						enabled: true, // enabled by default
						activityId: workflowData.workflowId,
						version: workflowData.workflowVersion,
						clientId: workflowData.clientId,
						policyId: workflowData.policyId
					}
				}
			};
			return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
				this.accountsService.mergeAccountWithModel(result);
				return true;
			});
		})
		.catch( err => {
			console.log("Cloning the workflow failed", err)
			throw err;
		})
	}

	getStatusString(account: Account): Promise<StatusString>
	{
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		return Promise.resolve(false); // should never be called, but just in case
	}

}
